/**
 * Records the filters a table settles on and hands back its recent ones.
 *
 * A filter is recorded when the user settled on it: left unchanged for
 * SETTLE_MS, used (a click in the table body — opening a row, a row
 * action), or left behind (the page, the table or the account changes).
 * Steps on the way are never recorded.
 *
 * Scoped per user and account (the store masks per user; the scope key
 * carries the account), so a DS switching child accounts sees each
 * account's own history.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { SETTLE_MS, filterSignature, getEntries, recordFilter } from '@shared/lib/filterHistory';

const EMPTY_SIGNATURE = '[]';

const useFilterHistory = ({ tableKey, filter }) => {
  const userUuid = useSelector(state => state.user?.user?.uuid);
  const accountUuid = useSelector(state => state.user?.metadata?.currentAccount?.uuid);
  const scope = tableKey && accountUuid ? `${accountUuid}:${tableKey}` : null;
  const enabled = Boolean(userUuid && scope && filter);

  // Updated after commit, not during render: React runs every cleanup of
  // a re-render before any setup, so the "leaving" cleanup below still
  // sees the filter that belonged to the scope being left.
  const latestFilter = useRef(filter);
  useEffect(() => {
    latestFilter.current = filter;
  }, [filter]);

  const record = useCallback(() => {
    if (enabled) recordFilter(userUuid, scope, latestFilter.current);
  }, [enabled, userUuid, scope]);

  const signature = filterSignature(filter);

  useEffect(() => {
    if (!enabled || signature === EMPTY_SIGNATURE) return undefined;
    const timerId = setTimeout(record, SETTLE_MS);
    return () => clearTimeout(timerId);
  }, [enabled, signature, record]);

  // Leaving: unmount, or the scope changing under a mounted table.
  useEffect(() => () => record(), [record]);

  // Read on demand (when the panel opens), without the filter shown now.
  const getRecent = useCallback(
    currentFilter => {
      if (!enabled) return [];
      const current = filterSignature(currentFilter);
      return getEntries(userUuid, scope).filter(entry => JSON.stringify(entry.filter) !== current);
    },
    [enabled, userUuid, scope]
  );

  return { getRecent, record };
};

export default useFilterHistory;
